import { arrayMove } from "@dnd-kit/sortable";
import { Constants } from "src/constant";
import type { ActivityActor } from "src/lib/activityService";
import { clearDraft as clearDraftService } from "src/lib/carouselPublicationService";
import {
  loadComposerDefaults,
  saveComposerDefaults,
  type ComposerDefaults,
} from "src/lib/happeningNowComposerDefaults";
import {
  platformBooleansFromDoc,
  platformsFromBooleans,
} from "src/lib/happeningNowPlatforms";
import {
  createHappeningNowItem,
  deleteHappeningNowItem,
  listHappeningNowItems,
  reorderHappeningNowItems,
  timestampToDatetimeLocalValue,
  updateHappeningNowItem,
} from "src/lib/happeningNowService";
import type { HappeningNowListItem } from "src/types/happeningNow";
import { create } from "zustand";

/**
 * A card in the composer. Two flavours share one shape:
 *   - committed — already in the Firestore draft (`committedId` set, `imageUrl`
 *     from the doc). Edits persist via updateHappeningNowItem.
 *   - staged — a newly dropped image not yet saved (`imageFile` + local
 *     `previewUrl`, `committedId` null). Commits via createHappeningNowItem.
 * `dirty` marks a committed card with unsaved edits.
 */
export type ComposerCard = {
  key: string;
  committedId: string | null;
  imageFile: File | null;
  previewUrl: string | null;
  imageUrl: string | null;
  dirty: boolean;
  title: string;
  description: string;
  targetLink: string;
  startsAtLocal: string;
  expiresAtLocal: string;
  active: boolean;
  platformAndroid: boolean;
  platformIos: boolean;
};

export type ComposerCardPatch = Partial<
  Pick<
    ComposerCard,
    | "title"
    | "description"
    | "targetLink"
    | "startsAtLocal"
    | "expiresAtLocal"
    | "active"
    | "platformAndroid"
    | "platformIos"
  >
>;

export type CardReadiness = { ready: true } | { ready: false; reason: string };

/** Preview URL for a card — the local object URL, else the committed image. */
export function composerCardImageSrc(card: ComposerCard): string {
  return card.previewUrl ?? card.imageUrl ?? "";
}

/** True for a not-yet-saved (staged) card. */
export function isStagedCard(card: ComposerCard): boolean {
  return card.committedId === null;
}

function isValidHttpUrl(value: string): boolean {
  try {
    const u = new URL(value.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/** Pure check used by the UI to badge a card and gate saving. */
export function cardReadiness(card: ComposerCard): CardReadiness {
  if (!card.imageFile && !card.imageUrl)
    return { ready: false, reason: "needs an image" };
  if (!card.title.trim()) return { ready: false, reason: "needs a title" };
  if (!isValidHttpUrl(card.targetLink))
    return { ready: false, reason: "needs a link" };
  if (!card.platformAndroid && !card.platformIos)
    return { ready: false, reason: "needs a platform" };
  const ms = new Date(card.expiresAtLocal).getTime();
  if (!card.expiresAtLocal || Number.isNaN(ms))
    return { ready: false, reason: "needs an expiry" };
  // Optional scheduled start, validated per card: valid, before this card's
  // expiry, and at most ~1 month out (mirrors parseStartsAt in the service).
  if (card.startsAtLocal) {
    const startMs = new Date(card.startsAtLocal).getTime();
    if (Number.isNaN(startMs)) return { ready: false, reason: "invalid start" };
    if (startMs >= ms) return { ready: false, reason: "start after expiry" };
    const maxStartMs =
      Date.now() +
      Constants.HAPPENING_NOW_MAX_START_AHEAD_DAYS * 24 * 60 * 60 * 1000;
    if (startMs > maxStartMs)
      return { ready: false, reason: "start too far ahead" };
  }
  return { ready: true };
}

const IMAGE_MAX_BYTES = Constants.HAPPENING_NOW_IMAGE_MAX_BYTES;

let cardSeq = 0;
function nextKey(): string {
  cardSeq += 1;
  return `staged-${cardSeq}`;
}

function newCardFromImage(
  file: File,
  defaults: ComposerDefaults,
): ComposerCard {
  return {
    key: nextKey(),
    committedId: null,
    imageFile: file,
    previewUrl: URL.createObjectURL(file),
    imageUrl: null,
    dirty: false,
    title: "",
    description: "",
    targetLink: "",
    startsAtLocal: "",
    expiresAtLocal: defaults.expiresAtLocal,
    active: true,
    platformAndroid: defaults.platformAndroid,
    platformIos: defaults.platformIos,
  };
}

/** Map an existing draft doc into a committed composer card. */
function committedCardFromDoc(doc: HappeningNowListItem): ComposerCard {
  const { android, ios } = platformBooleansFromDoc(doc.platforms);
  return {
    key: `committed-${doc.id}`,
    committedId: doc.id,
    imageFile: null,
    previewUrl: null,
    imageUrl: doc.imageUrl,
    dirty: false,
    title: doc.title,
    description: doc.description ?? "",
    targetLink: doc.targetLink,
    startsAtLocal: doc.startsAt
      ? timestampToDatetimeLocalValue(doc.startsAt)
      : "",
    expiresAtLocal: timestampToDatetimeLocalValue(doc.expiresAt),
    active: doc.active,
    platformAndroid: android,
    platformIos: ios,
  };
}

export type HappeningNowComposerStore = {
  defaults: ComposerDefaults;
  cards: ComposerCard[];
  selectedIndex: number;
  loadingDraft: boolean;
  committing: boolean;
  /** A drag has changed order since the last load — gates the save-time reorder. */
  orderDirty: boolean;
  error: string | null;
  success: string | null;

  initDefaults: () => void;
  patchDefaults: (patch: Partial<ComposerDefaults>) => void;

  /** Load the Firestore draft into the carousel, keeping staged + dirty cards. */
  loadDraft: () => Promise<void>;

  addImages: (files: File[]) => void;
  selectIndex: (index: number) => void;
  /** Reorder the carousel by drag; persists committed cards' order. */
  moveCard: (
    activeKey: string,
    overKey: string,
    actor: ActivityActor | null,
  ) => Promise<void>;
  patchSelected: (patch: ComposerCardPatch) => void;
  /** Drop a staged (unsaved) card locally. */
  removeStaged: (key: string) => void;
  /** Delete a committed card from the Firestore draft (confirms first). */
  deleteCommitted: (
    id: string,
    actor: ActivityActor | null,
  ) => Promise<boolean>;
  /** Drop all staged cards (committed ones stay). */
  clearStaged: () => void;
  /** Clear the whole working set: drop staged + delete the Firestore draft. */
  clearComposer: (actor: ActivityActor | null) => Promise<void>;

  clearFeedback: () => void;

  /**
   * Create every ready staged card and update every ready dirty committed card,
   * then refresh from Firestore. Returns the tally.
   */
  saveToDraft: (
    uid: string,
    creatorDisplayName: string,
    actor: ActivityActor | null,
  ) => Promise<{ created: number; updated: number }>;
};

export const useHappeningNowComposerStore = create<HappeningNowComposerStore>(
  (set, get) => ({
    defaults: { platformAndroid: true, platformIos: true, expiresAtLocal: "" },
    cards: [],
    selectedIndex: 0,
    loadingDraft: false,
    committing: false,
    orderDirty: false,
    error: null,
    success: null,

    initDefaults: () => set({ defaults: loadComposerDefaults() }),

    patchDefaults: (patch) =>
      set((s) => {
        const defaults = { ...s.defaults, ...patch };
        saveComposerDefaults(defaults);
        // The defaults bar is a master control: a change cascades to EVERY
        // card (re-syncing per-card overrides for that field). Committed cards
        // become dirty so the next Save pushes the change to Firestore.
        const touchesExpiry = patch.expiresAtLocal !== undefined;
        const touchesPlatforms =
          patch.platformAndroid !== undefined ||
          patch.platformIos !== undefined;
        const cards = s.cards.map((card) => {
          const next = { ...card };
          let changed = false;
          if (touchesExpiry) {
            next.expiresAtLocal = defaults.expiresAtLocal;
            changed = true;
          }
          if (touchesPlatforms) {
            next.platformAndroid = defaults.platformAndroid;
            next.platformIos = defaults.platformIos;
            changed = true;
          }
          if (changed && card.committedId) next.dirty = true;
          return next;
        });
        return { defaults, cards };
      }),

    loadDraft: async () => {
      set({ loadingDraft: true, error: null });
      try {
        const items = await listHappeningNowItems();
        set((s) => {
          // Preserve local edits to committed cards while reloading the rest.
          const dirtyById = new Map<string, ComposerCard>();
          s.cards.forEach((c) => {
            if (c.committedId && c.dirty) dirtyById.set(c.committedId, c);
          });
          const committed = items.map(
            (doc) => dirtyById.get(doc.id) ?? committedCardFromDoc(doc),
          );
          const staged = s.cards.filter((c) => c.committedId === null);
          const cards = [...committed, ...staged];
          return {
            cards,
            loadingDraft: false,
            // Server order is now the baseline — nothing to re-persist on save.
            orderDirty: false,
            selectedIndex: Math.max(
              0,
              Math.min(s.selectedIndex, cards.length - 1),
            ),
          };
        });
      } catch (err) {
        set({
          error: err instanceof Error ? err.message : "Could not load draft.",
          loadingDraft: false,
        });
      }
    },

    addImages: (files) =>
      set((s) => {
        const accepted: ComposerCard[] = [];
        let rejected = 0;
        for (const file of files) {
          if (!file.type.startsWith("image/") || file.size > IMAGE_MAX_BYTES) {
            rejected += 1;
            continue;
          }
          accepted.push(newCardFromImage(file, s.defaults));
        }
        if (accepted.length === 0) {
          return {
            error:
              rejected > 0 ? "Those files aren't images under 2 MB." : s.error,
          };
        }
        const cards = [...s.cards, ...accepted];
        return {
          cards,
          selectedIndex: s.cards.length,
          error:
            rejected > 0
              ? `${accepted.length} Images added. Skipped ${rejected} (Images must be under 2 MB).`
              : null,
        };
      }),

    selectIndex: (index) =>
      set((s) => ({
        selectedIndex: Math.max(0, Math.min(index, s.cards.length - 1)),
      })),

    moveCard: async (activeKey, overKey, actor) => {
      const { cards, selectedIndex } = get();
      const from = cards.findIndex((c) => c.key === activeKey);
      const to = cards.findIndex((c) => c.key === overKey);
      if (from < 0 || to < 0 || from === to) return;
      // Keep the same card selected across the reorder.
      const selectedKey = cards[selectedIndex]?.key;
      const next = arrayMove(cards, from, to);
      set({
        cards: next,
        selectedIndex: Math.max(
          0,
          next.findIndex((c) => c.key === selectedKey),
        ),
        orderDirty: true,
      });
      // Persist the committed cards' new relative order. Staged cards take
      // their final order when saved, so they don't persist here.
      const committedIds = next
        .filter((c) => c.committedId)
        .map((c) => c.committedId as string);
      if (committedIds.length > 1) {
        try {
          await reorderHappeningNowItems(committedIds, actor);
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "Could not reorder cards.",
          });
          await get().loadDraft();
        }
      }
    },

    patchSelected: (patch) =>
      set((s) => {
        const cards = s.cards.map((card, i) => {
          if (i !== s.selectedIndex) return card;
          const next = { ...card, ...patch };
          if (card.committedId) next.dirty = true;
          return next;
        });
        return { cards };
      }),

    removeStaged: (key) =>
      set((s) => {
        const target = s.cards.find((c) => c.key === key);
        if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
        const cards = s.cards.filter((c) => c.key !== key);
        return {
          cards,
          selectedIndex: Math.max(
            0,
            Math.min(s.selectedIndex, cards.length - 1),
          ),
        };
      }),

    deleteCommitted: async (id, actor) => {
      const card = get().cards.find((c) => c.committedId === id);
      const ok = window.confirm(
        `Remove "${
          card?.title || "this card"
        }" from the draft? It stays live until you publish again.`,
      );
      if (!ok) return false;
      set({ committing: true, error: null, success: null });
      try {
        await deleteHappeningNowItem(id, actor);
        await get().loadDraft();
        return true;
      } catch (err) {
        set({
          error: err instanceof Error ? err.message : "Could not remove card.",
        });
        return false;
      } finally {
        set({ committing: false });
      }
    },

    clearStaged: () =>
      set((s) => {
        s.cards
          .filter((c) => c.committedId === null && c.previewUrl)
          .forEach((c) => c.previewUrl && URL.revokeObjectURL(c.previewUrl));
        const cards = s.cards.filter((c) => c.committedId !== null);
        return {
          cards,
          selectedIndex: Math.max(
            0,
            Math.min(s.selectedIndex, cards.length - 1),
          ),
        };
      }),

    clearComposer: async (actor) => {
      // Drop staged cards locally first (revoke their object URLs).
      set((s) => {
        s.cards
          .filter((c) => c.committedId === null && c.previewUrl)
          .forEach((c) => c.previewUrl && URL.revokeObjectURL(c.previewUrl));
        return {
          cards: s.cards.filter((c) => c.committedId !== null),
          selectedIndex: 0,
        };
      });
      // Only committed cards remain — wipe them from the Firestore draft.
      if (get().cards.length === 0) return;
      set({ committing: true, error: null, success: null });
      try {
        const { count } = await clearDraftService(actor);
        await get().loadDraft();
        set({
          success: `Draft cleared (${count} ${
            count === 1 ? "card" : "cards"
          } removed). The live carousel stays until you publish.`,
        });
      } catch (err) {
        set({
          error:
            err instanceof Error ? err.message : "Could not clear the draft.",
        });
        await get().loadDraft();
      } finally {
        set({ committing: false });
      }
    },

    clearFeedback: () => set({ error: null, success: null }),

    saveToDraft: async (uid, creatorDisplayName, actor) => {
      const { cards } = get();
      const stagedReady = cards.filter(
        (c) => c.committedId === null && cardReadiness(c).ready,
      );
      const dirtyReady = cards.filter(
        (c) => c.committedId && c.dirty && cardReadiness(c).ready,
      );
      if (stagedReady.length === 0 && dirtyReady.length === 0) {
        set({
          error: "Nothing to save — new cards need details, or make an edit.",
        });
        return { created: 0, updated: 0 };
      }

      set({ committing: true, error: null, success: null });
      let created = 0;
      let updated = 0;
      let orderApplyFailed = false;
      const createdKeys: string[] = [];
      const keyToNewId = new Map<string, string>();
      const updatedIds = new Set<string>();
      try {
        // Sequential so `order` stays monotonic for the new cards.
        for (const card of stagedReady) {
          const platforms = platformsFromBooleans(
            card.platformAndroid,
            card.platformIos,
          );
          if (!platforms || !card.imageFile) continue;
          const { id: newId } = await createHappeningNowItem(
            {
              title: card.title,
              description: card.description || undefined,
              targetLink: card.targetLink,
              startsAtLocal: card.startsAtLocal || undefined,
              expiresAtLocal: card.expiresAtLocal,
              active: card.active,
              platforms,
              imageFile: card.imageFile,
              creatorDisplayName,
            },
            uid,
            actor,
          );
          created += 1;
          createdKeys.push(card.key);
          keyToNewId.set(card.key, newId);
        }

        for (const card of dirtyReady) {
          const platforms = platformsFromBooleans(
            card.platformAndroid,
            card.platformIos,
          );
          if (!platforms || !card.committedId) continue;
          await updateHappeningNowItem(
            card.committedId,
            uid,
            {
              title: card.title,
              description: card.description || undefined,
              targetLink: card.targetLink,
              startsAtLocal: card.startsAtLocal || undefined,
              expiresAtLocal: card.expiresAtLocal,
              active: card.active,
              platforms,
            },
            actor,
          );
          updated += 1;
          updatedIds.add(card.committedId);
        }

        // New cards are created with an append `order`. Only re-persist the
        // on-screen arrangement when a drag actually moved something
        // (`orderDirty`) — the common drop-and-save path already matches the
        // append order, so skip the extra batch write + reorder activity entry.
        if (created > 0 && get().orderDirty) {
          const orderedIds = cards
            .map((c) => c.committedId ?? keyToNewId.get(c.key) ?? null)
            .filter((id): id is string => id !== null);
          if (orderedIds.length > 1) {
            // Isolated: the cards are already saved by this point. A reorder
            // failure must NOT fall through to the outer catch — that would
            // skip the staged-card cleanup below and leave duplicates on
            // reload, under a misleading "couldn't save" error.
            try {
              await reorderHappeningNowItems(orderedIds, actor);
            } catch {
              orderApplyFailed = true;
            }
          }
        }

        // Drop the local staged cards we just created (they'll reload as
        // committed) and clear the dirty flag on saved committed cards, so the
        // upcoming loadDraft takes server truth instead of preserving them.
        set((s) => {
          const done = new Set(createdKeys);
          s.cards
            .filter((c) => done.has(c.key) && c.previewUrl)
            .forEach((c) => c.previewUrl && URL.revokeObjectURL(c.previewUrl));
          return {
            cards: s.cards
              .filter((c) => !done.has(c.key))
              .map((c) =>
                c.committedId && updatedIds.has(c.committedId)
                  ? { ...c, dirty: false }
                  : c,
              ),
          };
        });
        await get().loadDraft();

        const parts: string[] = [];
        if (created > 0) parts.push(`added ${created}`);
        if (updated > 0) parts.push(`updated ${updated}`);
        const remainingStaged = get().cards.filter(
          (c) => c.committedId === null,
        ).length;
        set({
          success: `Saved — ${parts.join(", ")}.${
            remainingStaged > 0 ? ` ${remainingStaged} still need details.` : ""
          }${
            orderApplyFailed
              ? " Card order couldn't be updated — try dragging again."
              : ""
          }`,
        });
        return { created, updated };
      } catch (err) {
        set({
          error:
            err instanceof Error ? err.message : "Could not save to draft.",
        });
        await get().loadDraft();
        return { created, updated };
      } finally {
        set({ committing: false });
      }
    },
  }),
);
